import styled, { keyframes } from 'styled-components';
import { HeroContainer, WavyBackground, ContentContainer, Title, Subtitle, PhoneButtonContainer } from './HeroSectionStyles';

// Animação de brilho para o contorno (mais intenso)
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
  50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
`;

// Animação de gradiente em movimento
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Animação de destaque para o texto
const textGlow = keyframes`
  0% { text-shadow: 0 0 5px rgba(64, 128, 255, 0.5); }
  50% { text-shadow: 0 0 15px rgba(0, 255, 255, 0.8), 0 0 25px rgba(64, 128, 255, 0.6); }
  100% { text-shadow: 0 0 5px rgba(64, 128, 255, 0.5); }
`;

// Animação de brilho para o contorno (mais sutil)
const glowPulseSubtle = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.3); }
  50% { box-shadow: 0 0 10px rgba(0, 255, 255, 0.5), 0 0 15px rgba(64, 128, 255, 0.2); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.3); }
`;

// Animação para revelar o texto da sigla
const revealText = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Animação para o efeito de digitalização
const typing = keyframes`
  0% { width: 0; }
  100% { width: 100%; }
`;

// Animação para o cursor piscando
const blink = keyframes`
  0%, 100% { border-color: transparent; }
  50% { border-color: #00FFFF; }
`;

// Animação para o brilho das linhas de conexão
const lineGlow = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.8; }
  100% { opacity: 0.3; }
`;

// Animação para o movimento de partículas de dados
const dataFlow = keyframes`
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  20% { opacity: 0.8; }
  80% { opacity: 0.8; }
  100% { transform: translateY(-20px) scale(1.2); opacity: 0; }
`;

// Animação para o scanner tecnológico
const scanEffect = keyframes`
  0% { transform: translateY(-100%); opacity: 0.5; }
  100% { transform: translateY(100%); opacity: 0; }
`;

// Animação para o efeito de partículas flutuantes
const particleFloat = keyframes`
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% { transform: translateY(-30px) translateX(20px); opacity: 0; }
`;

// Animação para o efeito de ondas de dados
const dataWave = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Animação para o efeito de holográfico
const holographicEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 100%; }
  100% { background-position: 0% 0%; }
`;

// Animação para o efeito de escaneamento de texto
const textScan = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

// Animação para o efeito de pulsação de energia
const energyPulse = keyframes`
  0% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(1); opacity: 0.3; }
`;

// Mensagem de confirmação
export const CallingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
  background: rgba(0, 32, 64, 0.3);
  border: 2px solid rgba(64, 128, 255, 0.5);
  border-radius: 16px;
  padding: 1.8rem 2.5rem;
  box-shadow: 0 0 20px rgba(0, 64, 255, 0.2);
  position: relative;
  overflow: hidden;
  animation: ${glowPulse} 3s infinite ease-in-out;
  
  @media (max-width: 768px) {
    position: relative;
    left: -10px;
    width: 100%;
    padding: 1.5rem 2rem;
    margin-left: auto;
    margin-right: auto;
  }
  
  /* Fundo com gradiente animado */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(0, 32, 64, 0.7) 0%, 
      rgba(0, 64, 128, 0.6) 25%,
      rgba(0, 96, 192, 0.5) 50%, 
      rgba(0, 64, 128, 0.6) 75%,
      rgba(0, 32, 64, 0.7) 100%);
    background-size: 400% 400%;
    animation: ${gradientMove} 10s ease infinite;
    z-index: -2;
  }
  
  /* Linhas tecnológicas */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      linear-gradient(90deg, rgba(64, 128, 255, 0.1) 1px, transparent 1px),
      linear-gradient(0deg, rgba(64, 128, 255, 0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    z-index: -1;
  }
`;

// Texto da mensagem de confirmação
export const CallingText = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 1px;
  font-family: 'Orbitron', sans-serif;
  text-align: center;
  line-height: 1.4;
  position: relative;
  padding-left: 32px;
  background: linear-gradient(to right, #64B6FF, #00FFFF, #64B6FF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% auto;
  animation: ${textGlow} 3s infinite ease-in-out, ${gradientMove} 5s linear infinite;
  
  /* Ícone de check posicionado à esquerda do texto */
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 22px;
    color: #00FFFF;
    opacity: 0.9;
    text-shadow: 0 0 10px rgba(0, 255, 255, 0.8);
    -webkit-text-fill-color: #00FFFF;
  }
`;

// Container para a seção de explicação da sigla
export const AcronymSection = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  position: relative;
  background: rgba(0, 16, 32, 0.4);
  border-radius: 16px;
  border: 1px solid rgba(64, 128, 255, 0.2);
  box-shadow: 0 0 20px rgba(0, 64, 255, 0.1);
  overflow: hidden;
  animation: ${glowPulseSubtle} 5s infinite ease-in-out;
  
  /* Efeito de scanner */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 20px;
    background: linear-gradient(to bottom, 
      rgba(0, 255, 255, 0) 0%,
      rgba(0, 255, 255, 0.2) 50%,
      rgba(0, 255, 255, 0) 100%);
    z-index: 1;
    animation: ${scanEffect} 3s infinite linear;
  }
  
  /* Linhas de grade tecnológica */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle, rgba(64, 128, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(64, 128, 255, 0.03) 1px, transparent 1px),
      linear-gradient(0deg, rgba(64, 128, 255, 0.03) 1px, transparent 1px);
    background-size: 20px 20px, 40px 40px, 40px 40px;
    background-position: 0 0, 0 0, 0 0;
    z-index: -1;
  }
`;

// Título da seção de explicação da sigla
export const AcronymTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  color: #64B6FF;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.5);
  position: relative;
  
  /* Linhas decorativas */
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    width: 60px;
    background: linear-gradient(to right, rgba(64, 128, 255, 0), #00FFFF, rgba(64, 128, 255, 0));
    animation: ${lineGlow} 3s infinite ease-in-out;
  }
  
  &::before {
    left: 50%;
    top: -10px;
    transform: translateX(-50%);
  }
  
  &::after {
    left: 50%;
    bottom: -10px;
    transform: translateX(-50%);
  }
`;

// Container para a lista de explicação da sigla
export const AcronymList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  
  /* Linha de conexão vertical */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 40px;
    width: 2px;
    height: 100%;
    background: linear-gradient(to bottom, 
      rgba(64, 128, 255, 0) 0%,
      rgba(64, 128, 255, 0.5) 10%,
      rgba(0, 255, 255, 0.7) 50%,
      rgba(64, 128, 255, 0.5) 90%,
      rgba(64, 128, 255, 0) 100%);
    z-index: 0;
  }
`;

// Item individual da sigla
export const AcronymItem = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
  padding-left: 60px;
  animation: ${props => props.isVisible ? revealText : 'none'} 0.5s ease-out forwards;
  animation-delay: ${props => props.index * 0.2}s;
  opacity: ${props => props.isVisible ? '1' : '0'};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  
  /* Nó de conexão */
  &::before {
    content: '';
    position: absolute;
    left: 36px;
    top: 10px;
    width: 10px;
    height: 10px;
    background: #00FFFF;
    border-radius: 50%;
    z-index: 1;
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.7);
  }
  
  /* Partículas de dados */
  &::after {
    content: '';
    position: absolute;
    left: 41px;
    top: 15px;
    width: 4px;
    height: 4px;
    background: #00FFFF;
    border-radius: 50%;
    z-index: 1;
    animation: ${props => props.isVisible ? dataFlow : 'none'} 2s infinite;
    animation-delay: ${props => props.index * 0.3}s;
  }
`;

// Letra da sigla
export const AcronymLetter = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #00FFFF;
  text-shadow: 0 0 15px rgba(0, 255, 255, 0.7);
  margin-right: 1.5rem;
  position: relative;
  opacity: ${props => props.isVisible ? '1' : '0'};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  animation: ${props => props.isVisible ? revealText : 'none'} 0.5s ease-out forwards;
  animation-delay: ${props => props.index * 0.1}s;
  
  /* Efeito de digitalização */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: #00FFFF;
    box-shadow: 0 0 5px rgba(0, 255, 255, 0.7);
  }
  
  /* Efeito de digitalização */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: #00FFFF;
    box-shadow: 0 0 5px rgba(0, 255, 255, 0.7);
  }
`;

// Texto da explicação
export const AcronymText = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: #64B6FF;
  line-height: 1.4;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  animation: ${props => props.isVisible ? typing : 'none'} 1s steps(30, end) forwards;
  animation-delay: ${props => (props.index * 0.2) + 0.3}s;
  width: 0;
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  border-right: 3px solid transparent;
  
  /* Efeito de cursor piscando */
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: #00FFFF;
    animation: ${props => props.isVisible ? blink : 'none'} 0.7s infinite;
    animation-delay: ${props => (props.index * 0.2) + 1.3}s;
  }
`;

// Container para a seção de descrição da BIANCA
export const DescriptionSection = styled.div`
  margin-top: 3rem;
  padding: 2.5rem;
  position: relative;
  background: rgba(0, 24, 48, 0.7);
  border-radius: 20px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  box-shadow: 0 0 30px rgba(0, 64, 255, 0.15);
  overflow: hidden;
  animation: ${glowPulseSubtle} 6s infinite ease-in-out;
  
  /* Efeito de grade holográfica */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(135deg, 
        rgba(0, 255, 255, 0.05) 25%, 
        transparent 25%, 
        transparent 50%, 
        rgba(0, 255, 255, 0.05) 50%, 
        rgba(0, 255, 255, 0.05) 75%, 
        transparent 75%, 
        transparent);
    background-size: 40px 40px;
    opacity: 0.3;
    z-index: -1;
    animation: ${holographicEffect} 15s infinite linear;
  }
  
  /* Efeito de ondas de dados */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 200%;
    height: 100%;
    background: linear-gradient(90deg,
      rgba(64, 128, 255, 0) 0%,
      rgba(64, 128, 255, 0.05) 20%,
      rgba(0, 255, 255, 0.1) 50%,
      rgba(64, 128, 255, 0.05) 80%,
      rgba(64, 128, 255, 0) 100%);
    z-index: -1;
    animation: ${dataWave} 8s infinite linear;
  }
`;

// Texto da descrição com efeito de escaneamento
export const DescriptionText = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  font-weight: 500;
  line-height: 1.8;
  letter-spacing: 0.5px;
  text-align: center;
  position: relative;
  margin: 0 auto;
  max-width: 90%;
  padding: 1.5rem;
  color: #FFFFFF;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  background: rgba(0, 16, 32, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  box-shadow: 0 0 20px rgba(0, 16, 32, 0.5) inset;
  
  /* Partículas de energia ao redor do texto */
  &::before, &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, 
      rgba(64, 128, 255, 0), 
      rgba(0, 255, 255, 0.7), 
      rgba(64, 128, 255, 0));
    left: 0;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

// Palavras destacadas
export const HighlightedWord = styled.span`
  position: relative;
  display: inline-block;
  font-weight: 700;
  font-size: 2rem;
  padding: 0 5px;
  background: linear-gradient(90deg,
    rgba(100, 182, 255, 1) 0%,
    rgba(0, 255, 255, 1) 50%,
    rgba(100, 182, 255, 1) 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${textScan} 5s infinite linear;
  text-shadow: 
    0 0 15px rgba(0, 255, 255, 0.8),
    -1px -1px 0 rgba(0, 255, 255, 0.3),
    1px -1px 0 rgba(0, 255, 255, 0.3),
    -1px 1px 0 rgba(0, 255, 255, 0.3),
    1px 1px 0 rgba(0, 255, 255, 0.3);
  
  /* Efeito de borda brilhante */
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg,
      rgba(100, 182, 255, 0.8) 0%,
      rgba(0, 255, 255, 1) 50%,
      rgba(100, 182, 255, 0.8) 100%);
    animation: ${textScan} 5s infinite linear;
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.8);
  }
`;

// Container para as partículas de energia
export const EnergyParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`;

// Partícula de energia individual
export const EnergyParticle = styled.div`
  position: absolute;
  width: ${props => props.size || '8px'};
  height: ${props => props.size || '8px'};
  border-radius: 50%;
  background: ${props => props.color || '#00FFFF'};
  box-shadow: 0 0 10px ${props => props.color || '#00FFFF'};
  top: ${props => props.top || '50%'};
  left: ${props => props.left || '50%'};
  opacity: 0;
  z-index: 2;
  animation: ${particleFloat} ${props => props.duration || '3s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

// Círculo de energia pulsante
export const EnergyCircle = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 2px solid rgba(0, 255, 255, 0.3);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  animation: ${energyPulse} 4s infinite ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    border: 1px solid rgba(64, 128, 255, 0.2);
    animation: ${energyPulse} 4s infinite ease-in-out;
    animation-delay: 1s;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    border-radius: 50%;
    border: 1px solid rgba(64, 128, 255, 0.1);
    animation: ${energyPulse} 4s infinite ease-in-out;
    animation-delay: 2s;
  }
`;

// Exportando os componentes do HeroSectionStyles para manter a consistência
export { HeroContainer, WavyBackground, ContentContainer, Title, Subtitle, PhoneButtonContainer };
